import React from 'react'
import { ModalOptions } from '@typesApp/PageFlow'
import { usePageFlow } from '@hooks/usePageFlow'
import { injectDataAttributes } from '@utils/dataAttributes'
import { IconProps } from '@components/ui/Icon'
import { Modal, ModalProps } from './Modal'
import { FooterAction } from './FooterAction'
import styles from '../../ui-css/Modal/styles/Modal.module.scss'

interface ActionProps {
  confirmLabel: string
  dismissLabel: string
  onConfirm: () => void
  onDismiss: () => void
  modalOptions?: ModalOptions
}

type IconType = IconProps['name'] | 'none'

const Actions: React.FC<ActionProps> = ({ confirmLabel, dismissLabel, onConfirm, onDismiss, modalOptions }) => {
  const showIcon = (show: boolean | undefined): boolean => show === true || show === undefined

  const secondary = {
    renderAs: modalOptions?.secondaryCta?.renderAs || 'button',
    underline: modalOptions?.secondaryCta?.underline === true,
    icon: showIcon(modalOptions?.secondaryCta?.showIcon) ? ('close-icon' as IconType) : 'none',
    sx: modalOptions?.secondaryCta?.sx,
    typographyColor: modalOptions?.secondaryCta?.typographyColor || 'inherit',
    typographyVariant: modalOptions?.secondaryCta?.typographyVariant,
  }

  const primary = {
    renderAs: modalOptions?.primaryCta?.renderAs || 'button',
    underline: modalOptions?.primaryCta?.underline === true,
    icon: showIcon(modalOptions?.primaryCta?.showIcon) ? ('checkmark-icon' as IconType) : 'none',
    sx: modalOptions?.primaryCta?.sx,
    typographyColor: modalOptions?.primaryCta?.typographyColor || 'inherit',
    typographyVariant: modalOptions?.primaryCta?.typographyVariant,
  }

  return (
    <div className={styles.actionContainer}>
      <FooterAction
        renderAs={secondary.renderAs}
        variant="secondary"
        color="secondary"
        icon={secondary.icon}
        underline={secondary.underline}
        typographyColor={secondary.typographyColor}
        typographyVariant={secondary.typographyVariant}
        sx={secondary.sx}
        label={confirmLabel}
        dataAttributes={injectDataAttributes('modal.confirm')}
        disableOnClick
        onClick={onConfirm}
      />
      <FooterAction
        renderAs={primary.renderAs}
        variant="primary"
        color="primary"
        icon={primary.icon}
        underline={primary.underline}
        typographyColor={primary.typographyColor}
        typographyVariant={primary.typographyVariant}
        sx={primary.sx}
        label={dismissLabel}
        dataAttributes={injectDataAttributes('modal.dismiss')}
        onClick={onDismiss}
      />
    </div>
  )
}

export interface ConfirmationModalProps extends Omit<ModalProps, 'onClose'> {
  confirmLabel: string
  dismissLabel: string
  onConfirm: () => void
  onDismiss: () => void
  message: string
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  confirmLabel,
  dismissLabel,
  onConfirm,
  onDismiss,
  message,
  ...rest
}) => {
  const { modalOptions } = usePageFlow()

  return (
    <Modal
      {...rest}
      onClose={onDismiss}
      footer={
        <Actions
          modalOptions={modalOptions}
          confirmLabel={confirmLabel}
          dismissLabel={dismissLabel}
          onConfirm={onConfirm}
          onDismiss={onDismiss}
        />
      }
    >
      {message && <span className={styles.textContent} dangerouslySetInnerHTML={{ __html: message }} />}
    </Modal>
  )
}
